import React, { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import ApiService from '@/services/api';

const COAModal = ({ isOpen, onClose, onSuccess, editData, cooperativeId }) => {
  const [loading, setLoading] = useState(false);
  const [accountGroups, setAccountGroups] = useState([]);
  const [formData, setFormData] = useState({
    account_code: '',
    account_name: '',
    account_type: 'ASSET',
    account_group: '',
    normal_balance: 'DEBIT',
    level: 1,
    parent_id: 0,
    opening_balance: 0,
    description: '',
    cooperative_id: cooperativeId
  });

  const accountTypes = [
    { value: 'ASSET', label: 'Aset (Harta)' },
    { value: 'LIABILITY', label: 'Kewajiban (Hutang)' },
    { value: 'EQUITY', label: 'Modal (Ekuitas)' },
    { value: 'REVENUE', label: 'Pendapatan' },
    { value: 'EXPENSE', label: 'Beban (Biaya)' },
    { value: 'COGS', label: 'Harga Pokok Penjualan' }
  ];

  const normalBalances = [
    { value: 'DEBIT', label: 'Debit' },
    { value: 'CREDIT', label: 'Kredit' }
  ];

  const groupAccounts = [
    { value: 'CURRENT_ASSET', label: 'Harta Lancar' },
    { value: 'FIXED_ASSET', label: 'Harta Tetap' },
    { value: 'OTHER_ASSET', label: 'Harta Lainnya' },
    { value: 'CURRENT_LIABILITY', label: 'Hutang Lancar' },
    { value: 'LONG_TERM_LIABILITY', label: 'Hutang Jangka Panjang' },
    { value: 'MEMBER_EQUITY', label: 'Modal Anggota' },
    { value: 'RETAINED_EARNINGS', label: 'Laba Ditahan' },
    { value: 'OPERATING_REVENUE', label: 'Pendapatan Operasional' },
    { value: 'NON_OPERATING_REVENUE', label: 'Pendapatan Non-Operasional' },
    { value: 'OPERATING_EXPENSE', label: 'Beban Operasional' },
    { value: 'NON_OPERATING_EXPENSE', label: 'Beban Non-Operasional' },
    { value: 'COST_OF_GOODS_SOLD', label: 'Harga Pokok Penjualan' }
  ];

  useEffect(() => {
    if (isOpen && cooperativeId) {
      fetchAccountGroups();
    }
  }, [isOpen, cooperativeId]);

  useEffect(() => {
    if (editData) {
      setFormData({
        account_code: editData.account_code || '',
        account_name: editData.account_name || '',
        account_type: editData.account_type || 'ASSET',
        account_group: editData.account_group || '',
        normal_balance: editData.normal_balance || 'DEBIT',
        level: editData.level || 1,
        parent_id: editData.parent_id || 0,
        opening_balance: editData.opening_balance || 0,
        description: editData.description || '',
        cooperative_id: cooperativeId
      });
    } else {
      setFormData({
        account_code: '',
        account_name: '',
        account_type: 'ASSET',
        account_group: '',
        normal_balance: 'DEBIT',
        level: 1,
        parent_id: 0,
        opening_balance: 0,
        description: '',
        cooperative_id: cooperativeId
      });
    }
  }, [editData, cooperativeId]);

  const fetchAccountGroups = async () => {
    try {
      const response = await ApiService.getChartOfAccountsForFilter(cooperativeId, '', 1, '');
      setAccountGroups(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch account groups:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'account_code') {
      // Remove any non-numeric characters (keep only digits)
      let cleanValue = value.replace(/[^\d]/g, '');
      
      // Limit to 4 digits maximum (1+1+2)
      cleanValue = cleanValue.slice(0, 4);
      
      // Auto-format to X.X.XX
      let formatted = '';
      if (cleanValue.length > 0) {
        // First digit
        formatted = cleanValue[0];
        
        if (cleanValue.length > 1) {
          // Add dot and second part (1 digit)
          formatted += '.' + cleanValue.slice(1, 2);
        }
        
        if (cleanValue.length > 2) {
          // Add dot and third part (2 digits)
          formatted += '.' + cleanValue.slice(2, 4);
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        cooperative_id: cooperativeId,
        account_code: formData.account_code,
        account_name: formData.account_name,
        account_type: formData.account_type,
        account_group: formData.account_group,
        normal_balance: formData.normal_balance,
        level: parseInt(formData.level) || 1,
        parent_id: formData.parent_id ? parseInt(formData.parent_id) : null,
        opening_balance: parseFloat(formData.opening_balance || 0),
        description: formData.description || null,
        is_active: true
      };

      if (editData) {
        await ApiService.updateChartOfAccount(editData.id, payload);
      } else {
        await ApiService.createChartOfAccount(payload);
      }

      // Reset form after successful save
      setFormData({
        account_code: '',
        account_name: '',
        account_type: 'ASSET',
        account_group: '',
        normal_balance: 'DEBIT',
        level: 1,
        parent_id: 0,
        opening_balance: 0,
        description: '',
        cooperative_id: cooperativeId
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Gagal menyimpan COA:', error);
      alert(error?.response?.data?.error || error?.message || 'Gagal menyimpan chart of account');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  {editData ? 'Edit Chart of Account' : 'Tambah Chart of Account'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-white hover:text-gray-200"
                >
                  <i className="mgc_close_line text-xl"></i>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Account Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kode Akun <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="account_code"
                    value={formData.account_code}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Contoh: 1.1.01"
                    pattern="^\d\.\d{1}\.\d{2}$"
                    title="Format: X.X.XX (contoh: 1.1.01)"
                    maxLength="8"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: X.X.XX (misal: 1.1.01, 2.3.15)</p>
                </div>

                {/* Account Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nama Akun <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Contoh: Kas"
                    required
                  />
                </div>

                {/* Account Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipe Akun <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="account_type"
                    value={formData.account_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {accountTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Kelompok Akun */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kelompok Akun <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="account_group"
                    value={formData.account_group}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Pilih Kelompok</option>
                    {groupAccounts.map(acc => (
                      <option key={acc.value} value={acc.value}>
                        {acc.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Normal Balance */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Saldo Normal <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="normal_balance"
                    value={formData.normal_balance}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {normalBalances.map(balance => (
                      <option key={balance.value} value={balance.value}>
                        {balance.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Parent Account (Akun Induk) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Akun Induk
                  </label>
                  <select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tidak Ada (Akun Header)</option>
                    {accountGroups.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.account_code} - {acc.account_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Kosongkan jika ini adalah akun tingkat pertama</p>
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Level Hirarki
                  </label>
                  <input
                    type="number"
                    name="level"
                    value={formData.level}
                    onChange={handleChange}
                    min="1"
                    max="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">1 = Header, 2 = Sub, dst.</p>
                </div>

                {/* Opening Balance */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Saldo Awal
                  </label>
                  <NumericFormat
                    value={formData.opening_balance || ''}
                    onValueChange={(values) => {
                      setFormData(prev => ({
                        ...prev,
                        opening_balance: values.value
                      }));
                    }}
                    onFocus={(e) => {
                      // Clear default 0 when focus
                      if (formData.opening_balance === 0 || formData.opening_balance === '0') {
                        setFormData(prev => ({
                          ...prev,
                          opening_balance: ''
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Set back to 0 if empty
                      if (!formData.opening_balance && formData.opening_balance !== 0) {
                        setFormData(prev => ({
                          ...prev,
                          opening_balance: 0
                        }));
                      }
                    }}
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="Rp "
                    allowNegative={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Rp 0"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deskripsi
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Deskripsi akun (opsional)"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 rounded-md text-white flex items-center gap-2 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <>
                    <i className="mgc_loading_line animate-spin"></i>
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <i className="mgc_save_line"></i>
                    {editData ? 'Update' : 'Simpan'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default COAModal;
